// Copyright ©️ 2026 eVolve MEP, LLC

import React from 'react';

import type { WorkOrderItem, WorkOrder } from '@evolvemep/api-ts';
import { Button, type MantineSize } from '@mantine/core';

import { GridMenu } from 'components/Mantine/GridMenu';
import { isNotNil } from 'helpers/isNotNil';
import { useIsMobile } from 'hooks/useIsMobile';
import { useHasAccess } from 'hooks/user/useHasAccess';
import { BendCreatorModal } from 'modules/Shop/EvolveFabrication/BendCreator/BendCreatorModal';
import { AddFabricationItemsToWorkOrderModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/AddFabricationItemsToWorkOrderModal';
import { AddWriteInItemToWorkOrderPopover } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/AddWriteInItemToWorkOrderPopover';
import { AddBOMItemsFromCatalogModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/AddBOMItemsFromCatalogModal';
import { AddItemsToWorkOrderFromCatalogModal } from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/AddItemsToWorkOrderFromCatalogModal';
import { useCanEditWorkOrder } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';

type Props = {
  workOrder: WorkOrder;
  parentWorkOrderItem?: WorkOrderItem;
  onItemsChanged: () => void;
  size?: MantineSize;
};

export const AddItemsToWorkOrderMenu = ({ workOrder, parentWorkOrderItem, onItemsChanged, size = 'xs' }: Props) => {
  const isMobile = useIsMobile();
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);
  const hasFabricationAccess = useHasAccess('fabrication', 'read');
  const hasFabricationFullAccess = useHasAccess('fabrication', 'write');

  if (!canEditWorkOrder('editItems')) return null;

  return (
    <GridMenu
      options={[
        { value: 'Add write-in item', icon: 'WriteInItem' },
        { value: 'Add from catalog', icon: 'Catalog' },
        { value: 'Add from fabrication item table', hidden: !hasFabricationAccess || isMobile, icon: 'Fabrication' },
        { value: 'Add from bend creator', hidden: !hasFabricationFullAccess || isMobile, icon: 'BendCreator' },
      ]}
      target={(toggle) => (
        <Button size={size} variant="light" onClick={toggle}>
          Add items
        </Button>
      )}
    >
      {(menuItemSelected, resetMenu) => (
        <>
          <AddWriteInItemToWorkOrderPopover
            opened={menuItemSelected === 'Add write-in item'}
            onClose={resetMenu}
            workOrder={workOrder}
            parentWorkOrderItem={parentWorkOrderItem}
            onItemAdded={onItemsChanged}
          />

          {isNotNil(parentWorkOrderItem) ? (
            <AddBOMItemsFromCatalogModal
              opened={menuItemSelected === 'Add from catalog'}
              onClose={resetMenu}
              workOrder={workOrder}
              parentWorkOrderItem={parentWorkOrderItem}
              onItemsChanged={onItemsChanged}
            />
          ) : (
            <AddItemsToWorkOrderFromCatalogModal
              opened={menuItemSelected === 'Add from catalog'}
              onClose={resetMenu}
              workOrder={workOrder}
              onItemsChanged={onItemsChanged}
            />
          )}

          {hasFabricationAccess && (
            <AddFabricationItemsToWorkOrderModal
              opened={menuItemSelected === 'Add from fabrication item table'}
              onClose={resetMenu}
              workOrder={workOrder}
              onItemsChanged={onItemsChanged}
            />
          )}

          {hasFabricationFullAccess && (
            <BendCreatorModal
              opened={menuItemSelected === 'Add from bend creator'}
              onClose={resetMenu}
              workOrder={workOrder}
              onBendsCreated={onItemsChanged}
            />
          )}
        </>
      )}
    </GridMenu>
  );
};
